import { DecimalPipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { Subscription } from 'rxjs';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { AcaoAlteracaoProdutoEnum } from '@shared/enums/compras/acao-alteracao-produto.enum';
import { MaskService } from '@shared/services/mask.service';
import { UnidadeMedidaService } from '@shared/services/apis/produto/unidade-medida.service';
import { LookupItemStruct } from '@shared/services/structs/pessoa/lookup-item.struct';
import { CardProduct, ProductSearchResult } from '../card-edit-dialog.types';
import { CedBuscaProdutoComponent } from './ced-busca-produto.component';

/**
 * Painel de produtos do card (Solicitação e Cotação): lista, busca no cadastro (app-ced-busca-produto),
 * produto avulso, quantidade e remoção.
 *
 * Contrato com a etapa:
 * - `products` é o array do shell; toda alteração emite `(productsChange)` com um array novo —
 *   a etapa repassa ao shell, que o envia no doSave e rebinda `[products]`.
 * - `(dirty)` marca alterações não salvas.
 * - `somenteLeitura` esconde adicionar/remover e deixa a quantidade só leitura.
 * - `avisoAlteracao`, quando devolve texto, pede confirmação antes de incluir ou remover (ex.:
 *   cotação já enviada ou respondida); sem texto, altera direto. Cancelar não muda nada.
 */
@Component({
  selector: 'app-ced-produtos',
  standalone: true,
  imports: [
    DecimalPipe,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    NgxCurrencyDirective,
    EmptyStateComponent,
    CedBuscaProdutoComponent,
  ],
  templateUrl: './ced-produtos.component.html',
  styleUrls: ['../ced-scrollbars.css', './ced-produtos.component.css', '../ced-painel-inclusao.css'],
})
export class CedProdutosComponent implements OnDestroy {
  @Input() products: CardProduct[] = [];
  @Input() somenteLeitura = false;
  /** Texto de confirmação para incluir/remover o produto; `null` altera sem perguntar. */
  @Input() avisoAlteracao?: (acao: AcaoAlteracaoProdutoEnum, p: CardProduct) => string | null;

  @Output() productsChange = new EventEmitter<CardProduct[]>();
  @Output() dirty = new EventEmitter<void>();

  // Product search
  itemAdding = false;
  @ViewChild(CedBuscaProdutoComponent) private buscaProduto?: CedBuscaProdutoComponent;
  @ViewChild('avulsoDescInput') private avulsoDescInput?: ElementRef<HTMLInputElement>;

  // Avulso
  avulsoMode = false;
  avulsoName = '';
  avulsoUnit = 'un';
  avulsoNote = '';
  unitOpts: { value: string; label: string }[] = [];

  private readonly subs = new Subscription();

  quantidadeMask: ReturnType<MaskService['getDynamicDecimalMask']>;
  /** Total com as mesmas casas da máscara de quantidade, sem zeros à direita nem resíduo de soma decimal. */
  formatoQuantidade: string;

  constructor(
    private unidadeMedidaService: UnidadeMedidaService,
    private maskService: MaskService,
    private dialog: MatDialog,
  ) {
    this.quantidadeMask = this.maskService.getDynamicDecimalMask();
    this.formatoQuantidade = `1.0-${this.quantidadeMask.precision}`;
    this.subs.add(
      this.unidadeMedidaService.getLookup().subscribe({
        next: (res) => {
          if (res.isError) return;
          this.unitOpts = (res.list || []).map((u: LookupItemStruct) => ({
            value: this.abreviacaoUnidade(u.descricao),
            label: u.descricao,
          }));
          if (this.unitOpts.length && !this.unitOpts.some(u => u.value === this.avulsoUnit)) {
            this.avulsoUnit = this.unitOpts[0].value;
          }
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get totalQty(): number {
    return this.products.reduce((s, p) => s + p.qty, 0);
  }

  /** Unidade do total só quando todos os produtos usam a mesma; com unidades misturadas, sem unidade. */
  get unidadeTotal(): string {
    const unidades = new Set(this.products.map(p => p.unit));
    return unidades.size === 1 ? [...unidades][0] : '';
  }

  isLowStock(p: CardProduct): boolean {
    return p.stock != null && p.stock < p.qty;
  }

  updateQuantity(product: CardProduct, value: number | string | null): void {
    // Espelha exatamente o que a máscara emitiu. O clamp anterior (`Math.max(1, …)`) divergia da
    // view: o binding é one-way, então corrigir 0,50 para 1 não reescrevia o campo — a tela
    // mostrava 0,50 e a API recebia 1. Quantidade inválida é barrada no salvamento.
    const quantity = Number(value) || 0;
    this.products = this.products.map(p => p === product ? { ...p, qty: quantity } : p);
    this.productsChange.emit(this.products);
    this.dirty.emit();
  }

  /** A busca vive dentro do @if do painel: abrir recria a busca vazia e já focada; fechar a descarta. */
  toggleAddingItem(): void {
    this.itemAdding = !this.itemAdding;
    this.avulsoMode = false;
  }

  addProductFromSearch(r: ProductSearchResult): void {
    const novo: CardProduct = {
      idProduto: r.idProduto ?? null,
      sku: r.sku,
      name: r.name,
      marca: r.marca ?? null,
      qty: 1,
      unit: r.unit,
      stock: r.stock,
      price: r.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
      valorUnitario: r.price,
      isAvulso: false,
    };
    // A busca já volta limpa ao escolher; se cancelar, o foco volta ao campo para buscar de novo.
    this.confirmarAlteracao(AcaoAlteracaoProdutoEnum.Incluir, novo, () => {
      this.products = [...this.products, novo];
      this.productsChange.emit(this.products);
      this.itemAdding = false;
      this.dirty.emit();
    }, () => this.buscaProduto?.focar());
  }

  get avulsoValid(): boolean {
    return this.avulsoName.trim().length > 1;
  }

  startAvulso(): void {
    this.avulsoName = (this.buscaProduto?.query || '').trim();
    this.avulsoMode = true;
    this.avulsoUnit = this.unitOpts[0]?.value || 'un';
    this.avulsoNote = '';
    setTimeout(() => this.avulsoDescInput?.nativeElement.focus());
  }

  /** Volta para a busca do cadastro sem fechar o painel (a busca é recriada vazia e já focada). */
  cancelAvulso(): void {
    this.avulsoMode = false;
  }

  confirmAvulso(): void {
    if (!this.avulsoValid) return;
    const novo: CardProduct = {
      idProduto: null,
      sku: '',
      name: this.avulsoName.trim(),
      qty: 1,
      unit: this.avulsoUnit,
      stock: null,
      price: '0,00',
      valorUnitario: null,
      isAvulso: true,
      observacao: this.avulsoNote.trim() || null,
    };
    // Cancelar mantém o formulário do avulso preenchido.
    this.confirmarAlteracao(AcaoAlteracaoProdutoEnum.Incluir, novo, () => {
      this.products = [...this.products, novo];
      this.productsChange.emit(this.products);
      this.avulsoMode = false;
      this.itemAdding = false;
      this.dirty.emit();
    });
  }

  removeProduct(product: CardProduct): void {
    // Remove por idCompraItem quando o produto já foi salvo (o shell recria os objetos após salvar, então a
    // referência capturada pode não existir mais com o confirm aberto); sem id, remove pela referência.
    this.confirmarAlteracao(AcaoAlteracaoProdutoEnum.Remover, product, () => {
      this.products = product.idCompraItem != null
        ? this.products.filter(p => p.idCompraItem !== product.idCompraItem)
        : this.products.filter(p => p !== product);
      this.productsChange.emit(this.products);
      this.dirty.emit();
    });
  }

  /** Pergunta antes de incluir/remover quando a etapa devolve aviso; sem aviso, efetiva direto. */
  private confirmarAlteracao(acao: AcaoAlteracaoProdutoEnum, produto: CardProduct, efetivar: () => void, aoCancelar?: () => void): void {
    const aviso = this.avisoAlteracao?.(acao, produto) ?? null;
    if (!aviso) { efetivar(); return; }

    const ref = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: acao === AcaoAlteracaoProdutoEnum.Incluir ? 'Incluir produto' : 'Remover produto',
        description: aviso,
        buttonConfirm: acao === AcaoAlteracaoProdutoEnum.Incluir ? 'Incluir' : 'Remover',
        buttonClose: 'Cancelar',
      },
    });
    this.subs.add(
      ref.afterClosed().subscribe(result => {
        if (result?.confirm) efetivar();
        else aoCancelar?.();
      }),
    );
  }

  private abreviacaoUnidade(descricao: string): string {
    const idx = descricao.indexOf(' - ');
    return (idx > 0 ? descricao.slice(0, idx) : descricao).trim();
  }
}
